import { ENTRY_MODE_TITLES, type MeResponse, PLATFORM_ROLE_TITLES } from '@epc/contracts';
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ShieldCheck } from 'lucide-react';
import { useEffect } from 'react';
import { api } from '../lib/api';
import { dateTime } from '../lib/format';
import { meQuery, useMe } from '../lib/session';
import { Button } from '../ui/button';
import { toast } from '../ui/feedback';

/**
 * Плашка входа команды портала в компанию (ЛЦ-11): в какой компании, кем, в каком режиме и до какого времени —
 * на каждой странице, поверх шапки, с «Выйти из компании».
 */
async function reloadAll(qc: QueryClient) {
  await qc.fetchQuery({ ...meQuery, staleTime: 0 });
  void qc.resetQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
}

export function PlatformEntryBanner() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const entry = me.platformEntry;
  const exit = useMutation({
    mutationFn: () => api<MeResponse>('/platform/entries/exit', { method: 'POST' }),
    onSuccess: (next) => {
      const id = entry?.tenantId;
      qc.clear();
      qc.setQueryData(['me'], next);
      if (id) navigate({ to: '/platform/tenants/$id', params: { id } });
      else navigate({ to: '/platform' });
      toast('Вы вышли из компании и снова в своей');
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не удалось выйти из компании', 'danger'),
  });
  const until = entry?.expiresAt;
  // В назначенное время вход заканчивается сам: портал перечитывает права и возвращает в свою компанию
  useEffect(() => {
    if (!until) return;
    const ms = Math.max(0, Date.parse(until) - Date.now()) + 1500;
    if (ms > 2_147_000_000) return;
    const t = setTimeout(() => void reloadAll(qc), ms);
    return () => clearTimeout(t);
  }, [until, qc]);
  if (!entry) return null;
  const view = entry.mode === 'view';
  return (
    <div className="border-b border-accent/50 bg-accent-soft text-ink print:hidden" role="status">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 md:px-8">
        <ShieldCheck className="size-5 shrink-0 text-accent-text" aria-hidden />
        <div className="min-w-0 flex-1 basis-[220px] text-[13px] leading-5">
          <span className="font-semibold">
            Вы в компании «{entry.tenantName}» как {PLATFORM_ROLE_TITLES[entry.role].toLowerCase()}.
          </span>{' '}
          <span className="text-ink-2">
            {view ? 'Только просмотр — изменения отключены' : 'Правка — изменения пишутся в историю компании'}
            {entry.money === 'none' ? ', суммы скрыты' : ''}. Вход закончится сам в{' '}
            {dateTime(entry.expiresAt)}.
          </span>
        </div>
        <Button
          size="sm"
          variant="secondary"
          loading={exit.isPending}
          onClick={() => exit.mutate()}
          title={`Режим: ${ENTRY_MODE_TITLES[entry.mode]}`}
        >
          Выйти из компании
        </Button>
      </div>
    </div>
  );
}
